import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSiteContent } from '../hooks/useSiteContent';
import { smoothScrollToHash, isScrolling } from '../lib/smoothScroll';
import { ENROLLMENT_URL, openEnrollment } from '../lib/enrollment';

const LOGO_URL = '/logo.png';

// Map non-homepage URL paths to the nav item they should activate.
// Any URL not in this map and not on `/` will leave the nav un-highlighted.
const PATH_TO_NAV = [
  { test: (p) => p.startsWith('/membership'), id: 'memberships' },
];

export default function Navbar() {
  const { content } = useSiteContent();
  const navItems = content.navItems || [];
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  // ---------- Combined scroll handler: header style + scroll-spy + URL-hash sync ----------
  // The spy picks the **bottommost section whose top has crossed the probe
  // line** (NAV_PROBE_Y below the viewport top). This works for both
  // programmatic anchor jumps (where sections land at y≈80 under the navbar)
  // and natural wheel/touch scrolling — and is tolerant of section
  // `scroll-margin-top`, padding, or `content-visibility:auto` height estimates.
  //
  // ALSO writes the active section id into `window.location.hash` via
  // replaceState — but DEBOUNCED. The hash only updates after the user
  // has been viewing the same section for HASH_SETTLE_MS (300ms) of
  // stable scroll. This prevents brief transitions during a smooth-
  // scroll convergence (or a button-into-view auto-scroll just before
  // a navigate() call) from polluting the URL with whichever section
  // the spy momentarily thought was active.
  //
  // The semantic value: the URL always reflects "the section the user
  // was last lingering on", which is the right thing to restore when
  // they press browser BACK from a route push.
  useEffect(() => {
    let pending = false;
    const NAV_PROBE_Y = 140;
    const HASH_SETTLE_MS = 300;
    let lastSyncedHash = window.location.hash;
    let pendingHash = lastSyncedHash;
    let settleTimer = 0;

    const flushHash = () => {
      settleTimer = 0;
      if (pendingHash === lastSyncedHash) return;
      lastSyncedHash = pendingHash;
      try {
        const newUrl = `${window.location.pathname}${window.location.search}${pendingHash}`;
        window.history.replaceState(window.history.state, '', newUrl);
      } catch (_e) { /* ignore */ }
    };

    const update = () => {
      pending = false;
      setScrolled(window.scrollY > 30);

      if (pathname !== '/') return;
      if (!navItems.length) return;

      let best = null;
      let bestDistance = Infinity;
      for (const item of navItems) {
        const id = item.href.replace('#', '');
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= NAV_PROBE_Y) {
          best = id;
          bestDistance = NAV_PROBE_Y - top;
        } else {
          if (!best) {
            const dist = top - NAV_PROBE_Y;
            if (dist < bestDistance) {
              best = id;
              bestDistance = dist;
            }
          }
          break;
        }
      }
      const doc = document.documentElement;
      const scrollMax = (doc.scrollHeight || 0) - window.innerHeight;
      if (scrollMax > 0 && window.scrollY >= scrollMax - 30) {
        best = navItems[navItems.length - 1]?.href.replace('#', '') || best;
      }
      if (!best) best = navItems[0]?.href.replace('#', '') || 'home';
      setActiveId(best);

      // Debounced hash sync — only commit after HASH_SETTLE_MS of
      // stable scroll on the same target section. Cancels any pending
      // commit when the target changes mid-flight. Also: skip entirely
      // while a programmatic smooth-scroll is running, otherwise we'd
      // replaceState through every transitional section we pass and
      // overwrite the pushState the click handler just made.
      if (isScrolling()) return;
      const desiredHash = window.scrollY < 80 ? '' : `#${best}`;
      if (desiredHash !== pendingHash) {
        pendingHash = desiredHash;
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(flushHash, HASH_SETTLE_MS);
      }
    };

    const onScroll = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, [navItems, pathname]);

  // ---------- Route-driven active id for non-homepage pages ----------
  // On `/membership/*` (or any future route mapped in PATH_TO_NAV) the
  // highlight reflects the page the user is on, not the last section
  // they happened to scroll past on the homepage before navigating.
  useEffect(() => {
    if (pathname === '/' || pathname === '') return;
    const match = PATH_TO_NAV.find((m) => m.test(pathname));
    if (match) setActiveId(match.id);
  }, [pathname]);

  // Programmatic nav. Delegates HISTORY entry creation to react-router's
  // `navigate()` so its internal history stays in sync with window.history
  // — that's the only reliable way iOS Safari's bottom-bar BACK arrow
  // fires `popstate` AND `useLocation` updates. Manual `pushState` +
  // synthetic HashChangeEvent works in Chromium-based Playwright but
  // not in real Safari (react-router-dom v6 doesn't listen to
  // hashchange — only popstate).
  //
  // After navigate(), we ALSO kick off the smooth-scroll synchronously
  // — bypassing React's effect scheduler. Without this, on a busy
  // main thread (CustomCursor + HeroDemoStrip + EmberField RAF loops
  // all running), the Home hash-effect can be queued ~1 s behind the
  // click, producing a sluggish "click did nothing for a second"
  // feeling. Home's effect still fires on POP (browser BACK/FORWARD)
  // — that's where it does its actual work. For PUSH from a click,
  // we beat it to the punch and the effect's `isScrolling()` guard
  // (see App.js Home) short-circuits the redundant restart.
  //
  // Same-hash repeats are de-duped so clicking the same nav tab twice
  // doesn't create duplicate history entries.
  const scrollTo = (e, href) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    setOpen(false);
    const id = href.slice(1);
    const targetUrl = `/${href}`; // e.g., "/#memberships"
    const currentMatches =
      pathname === '/' && (window.location.hash === href || (!window.location.hash && id === 'home'));
    if (!currentMatches) {
      navigate(targetUrl);
      // Kick off smooth-scroll NOW — don't wait for the effect cycle.
      smoothScrollToHash(href, { history: 'none' });
      return;
    }
    // Same hash AND same page — still smooth-scroll to the section in
    // case the user has scrolled away from it. No history entry needed.
    smoothScrollToHash(href, { history: 'none' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-red-600/20' : 'bg-transparent'}`}>
      {/* Container width widened + padding scales down at xl so the 10-item
          desktop nav + JOIN NOW can fit cleanly from 1280px upward. */}
      <div className="max-w-[1600px] mx-auto px-4 xl:px-5 2xl:px-10 flex items-center justify-between h-20">
        <a href="#home" onClick={(e) => scrollTo(e, '#home')} className="flex items-center gap-3 group flex-shrink-0" data-cursor="hover">
          <div className="relative logo-pulse-once w-12 h-12 flex-shrink-0">
            <div className="absolute inset-0 bg-red-600/30 blur-md rounded-lg group-hover:bg-red-600/50 transition" />
            <img
              src={LOGO_URL}
              alt="Fitness Pro"
              width="48"
              height="48"
              decoding="async"
              fetchPriority="high"
              onError={(e) => {
                // Hide the failed <img> and reveal the styled "FP" badge
                // so the navbar never shows a broken-image icon.
                e.currentTarget.style.display = 'none';
                const fb = e.currentTarget.nextElementSibling;
                if (fb && fb.classList.contains('logo-img-fallback')) {
                  fb.style.opacity = '1';
                }
              }}
              className="relative w-12 h-12 object-contain drop-shadow-[0_0_8px_rgba(239,43,43,0.4)] transition-transform duration-300 ease-out group-hover:rotate-[5deg] group-hover:scale-110"
            />
            {/* Graceful fallback — styled red "FP" monogram if the logo
                image ever fails. Hidden by default; revealed by onError. */}
            <div
              aria-hidden
              className="logo-img-fallback absolute inset-0 flex items-center justify-center rounded-lg bg-red-600/15 border border-red-600/50 opacity-0 transition-opacity duration-200"
            >
              <span className="font-display text-lg tracking-tight text-red-500">FP</span>
            </div>
          </div>
          <div className="flex flex-col leading-none whitespace-nowrap">
            <span className="font-display text-xl tracking-widest">FITNESS<span className="text-red-500">PRO</span></span>
            <span className="text-[9px] tracking-[0.3em] text-zinc-500 mt-0.5">GALT · SINCE 1992</span>
          </div>
        </a>

        {/* Desktop nav — shown from xl (1280px) upward. Spacing/typography
            scales: tight at xl so 10 items fit on a 1280 laptop, premium
            at 2xl+. The JOIN NOW CTA sits alongside and never clips. */}
        <nav className="hidden xl:flex items-center gap-3 2xl:gap-6 flex-1 justify-center">
          {navItems.map((item) => {
            const isActive = activeId === item.href.replace('#', '');
            return (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollTo(e, item.href)}
                aria-current={isActive ? 'page' : undefined}
                data-cursor="hover"
                className={`draw-line whitespace-nowrap text-[11px] 2xl:text-[13px] tracking-[0.12em] 2xl:tracking-[0.2em] font-semibold transition-colors ${isActive ? 'text-white nav-active-glow' : 'text-zinc-200 hover:text-white'}`}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
          <a
            href={`${ENROLLMENT_URL}&plan=navbar-cta`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); openEnrollment({ plan: 'navbar-cta' }); }}
            data-cursor="hover"
            data-testid="navbar-join-now"
            className="btn-shimmer relative inline-flex items-center justify-center whitespace-nowrap px-4 py-2.5 2xl:px-6 2xl:py-3 bg-red-600 hover:bg-red-500 text-white text-[11px] 2xl:text-xs tracking-[0.16em] 2xl:tracking-[0.2em] font-bold rounded-md transition-colors"
          >
            JOIN NOW
          </a>
        </div>

        {/* Hamburger menu — shown below xl (1279px and narrower). The
            drawer includes JOIN NOW at the bottom so it's never hidden. */}
        <button data-cursor="hover" onClick={() => setOpen(!open)} className="xl:hidden text-white p-2" aria-label="Toggle menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile / tablet drawer — animated open/close, instant tap. */}
      <div
        className={`xl:hidden bg-black/95 backdrop-blur-xl border-t border-red-600/20 overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${open ? 'max-h-[80vh] opacity-100 pointer-events-auto' : 'max-h-0 opacity-0 pointer-events-none border-t-0'}`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {navItems.map((item) => {
            const isActive = activeId === item.href.replace('#', '');
            return (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollTo(e, item.href)}
                aria-current={isActive ? 'page' : undefined}
                className={`text-sm tracking-[0.2em] transition-colors ${isActive ? 'text-red-500' : 'text-zinc-300 hover:text-red-500'}`}
              >
                {item.label}
              </a>
            );
          })}
          <a
            href={`${ENROLLMENT_URL}&plan=navbar-cta`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); setOpen(false); openEnrollment({ plan: 'navbar-cta' }); }}
            data-testid="navbar-join-now-mobile"
            className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white text-xs tracking-[0.2em] font-bold rounded-md"
          >
            JOIN NOW
          </a>
        </div>
      </div>
    </header>
  );
}
